import React, { Suspense, useContext, useMemo } from 'react'
import { Provider } from 'jotai'
import { Box, RowsSkeletonLoader, Sheet } from '@node-space/ui-components'
import { ContactAssignSearch } from 'pages/Contacts/ContactAssignSearch'
import { ChannelEditForm } from 'pages/ManageChannels/components/ChannelEditForm'
import { ChannelPayment } from 'pages/Payments/ChannelPayments/ChannelPayment'
import { PaymentLink } from 'pages/Payments/PaymentLinks/PaymentLink'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ContactAtomType } from 'types/atoms/atom.contacts'
import { ActionIds } from 'types/types'
import { Convert } from '../ConvertV2/Convert'
import { FiatDeposit } from '../FiatDeposit/FiatDeposit'
import FiatSend from '../FiatSend/FiatSend'
import { WalletCreateForm } from '../Form/WalletCreateForm'
import { WalletEditForm } from '../Form/WalletEditForm'
import { Move } from '../Move/Move'
import { SendMFE } from '../Send/SendMFE'

interface WalletSheetProps {
  isSheetOpen: boolean
}

export const WalletSheet = ({ isSheetOpen }: WalletSheetProps) => {
  const walletAction = useContext(WalletActionContext)?.[0] ?? null
  const { onBack, onClose, header, subHeader } = walletAction || {}

  const sheetContent = useMemo(() => {
    switch (walletAction?.actionId) {
      case ActionIds.MOVE:
        return <Move />
      case ActionIds.FIAT_DEPOSIT:
        return <FiatDeposit />
      case ActionIds.SEND:
        return (
          <Suspense
            fallback={
              <Box width="full">
                <RowsSkeletonLoader rows={5} />
              </Box>
            }
          >
            <SendMFE />
          </Suspense>
        )
      case ActionIds.CONVERT:
        return <Convert />
      case ActionIds.FIAT_SEND:
        return <FiatSend />
      case ActionIds.PAYMENT_LINK:
        return <PaymentLink />
      case ActionIds.CHANNEL_PAYMENT:
        return <ChannelPayment />
      case ActionIds.CHANNEL_EDIT:
        return <ChannelEditForm />
      case ActionIds.CONTACT_ASSIGN:
        return <ContactAssignSearch atomType={ContactAtomType.WALLET_ACTION} />
      case ActionIds.WALLET_EDIT:
        return <WalletEditForm />
      case ActionIds.WALLET_CREATE:
        return <WalletCreateForm />
      default:
        return null
    }
  }, [walletAction])

  const handleSheetClose = () => {
    onClose?.()
  }

  return (
    <Sheet
      header={header}
      onBack={onBack}
      isOpen={isSheetOpen}
      onClose={handleSheetClose}
      subHeader={subHeader}
    >
      <Provider>{sheetContent}</Provider>
    </Sheet>
  )
}
